// @flow
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
  useEffect
} from "react";
import { createPortal } from "react-dom";
import LocationSearchAPI from "./locationSearchAPI";
import useSynchronisedState from "../../hooks/useSynchronisedState";
import LocationSearchInputResult from "../LocationSearchInputResult";
import ApiContext from "../ApiContext";
import Typography from "../Typography";
import useOutsideClickHandler from "../../hooks/useOutsideClickHandler";
import BasicButton from "../BasicButton";
import GeoLocationArrowIcon from "../Icons/GeoLocationArrowIcon";
import MagnifyingGlassIcon from "../Icons/MagnifyingGlassIcon";
import LoadingDial from "../LoadingDial";
import ConditionalWrapper from "../ConditionalWrapper";
import useMatchMediaMatches from "../../hooks/useMatchMediaMatches";
import useModalHook from "../../hooks/useModalHook";
import Button from "../Button";

import styles from "./locationSearchInput.module.scss";

type Item = {
  description: string,
  html: string,
  place_id: string,
  type: "google" | "justpark" | "message"
};

type UIProps = {
  wrapperRef: any,
  query: string,
  results: Array<Item>,
  activeItem: Item,
  onSearchQueryChange: (string) => void,
  onKeyDown: (SyntheticKeyboardEvent<*>) => void,
  setActiveIndex: (number) => void,
  selectItem: (number) => void,
  onClickGeoLocation: () => void,
  onFocus: () => void,
  showResultsContainer: boolean,
  isLoading: boolean,
  inputRef: any,
  isFullscreen: boolean,
  onCancel: () => void,
  invalid: boolean
};

const cypress = "location-search-input";

export const LocationSearchInputUI = ({
  wrapperRef,
  results,
  query,
  onSearchQueryChange,
  onKeyDown,
  activeItem,
  setActiveIndex,
  selectItem,
  onFocus,
  showResultsContainer,
  onClickGeoLocation,
  isLoading,
  inputRef,
  isFullscreen,
  onCancel,
  invalid
}: UIProps) => {
  const baseClass = isFullscreen ? styles.mobileFullscreen : styles.base;

  return (
    <div className={baseClass} ref={wrapperRef} data-cy={cypress}>
      <div className={styles.labelWrapper}>
        <label className={styles.label}>
          <div className={styles.inputContainer}>
            <Typography
              color={invalid ? "error" : "brand"}
              className={styles.labelText}
            >
              Park at
            </Typography>
            <input
              type="text"
              autoComplete="off"
              spellCheck="false"
              ref={inputRef}
              className={styles.input}
              value={query}
              data-cy="search-input"
              onChange={(event) => {
                onSearchQueryChange(event.target.value);
              }}
              onKeyDown={onKeyDown}
              onFocus={onFocus}
              id="search"
              placeholder="Enter location or location ID"
              data-hj-allow="true"
            />
          </div>
          {isLoading ? (
            <div data-cy="search-loader" className={styles.loadingIcon}>
              <LoadingDial />
            </div>
          ) : (
            <MagnifyingGlassIcon className={styles.searchIcon} />
          )}
        </label>
        <Button
          type="button"
          text
          onClick={onCancel}
          className={styles.cancelButton}
        >
          Cancel
        </Button>
      </div>

      {showResultsContainer ? (
        <div
          className={styles.resultsContainer}
          data-cy="search-results-container"
        >
          <BasicButton onClick={onClickGeoLocation} cypress="get-geolocation">
            <div className={styles.geoLocationItem}>
              <GeoLocationArrowIcon />{" "}
              <Typography>Use current location</Typography>
            </div>
          </BasicButton>

          {results.map((resultItem, index) => {
            const { html, place_id: placeId, type } = resultItem;
            const isActive = activeItem === resultItem;

            return (
              <LocationSearchInputResult
                type={type}
                key={placeId}
                isActive={isActive}
                description={html}
                onMouseOver={() => setActiveIndex(index)}
                onClick={() => {
                  selectItem(index);
                }}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

type Props = {
  value: string,
  onChange: (
    string,
    { lat: string, lng: string } | { listingId: string }
  ) => void,
  invalid: boolean,
  searchInputRef: any
};

const loadingMessage = [
  {
    type: "loading",
    html: "Searching&hellip;",
    description: "Searching",
    place_id: ""
  }
];

const LocationSearchInput = ({
  value,
  onChange,
  searchInputRef,
  invalid
}: Props) => {
  const apiContext = useContext(ApiContext);

  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useSynchronisedState(value);

  const locationApi = useMemo(
    () => new LocationSearchAPI(apiContext?.jpApiClient),
    [apiContext]
  );

  const [showResultsContainer, setShowResultsContainer] = useState(false);

  const onSearchQueryChange = useCallback(
    (text) => {
      setQuery(text);

      const hasLength = text.length > 0;

      setIsLoading(hasLength);

      locationApi.search(text).then((data) => {
        setIsLoading(false);
        setActiveIndex(0);
        setResults(data);
        setShowResultsContainer(true);
      });

      if (hasLength === false) {
        setResults([]);
      }
    },
    [locationApi, setQuery, setIsLoading, setActiveIndex]
  );

  const activeItem = useMemo(() => {
    const remainder = activeIndex % (results.length * Math.sign(activeIndex));
    return results.at(remainder);
  }, [results, activeIndex]);

  const cycleActiveIndex = useCallback(
    (direction) => {
      setActiveIndex((activeValue) => activeValue + direction);
    },
    [setActiveIndex]
  );

  const onItemSelect = useCallback(
    (index) => {
      if (index === -1) {
        return;
      }

      const item = index ? results[index] : activeItem;

      if (item) {
        const { description, place_id: placeId, type } = item;

        setQuery(description);
        setShowResultsContainer(false);

        if (type === "google") {
          locationApi.googleDetails(placeId).then((data) => {
            onChange(description, {
              ...data.coords,
              googlePlaceId: data.googlePlaceId
            });
          });
        }

        if (type === "justpark") {
          onChange(description, { listingId: placeId });
        }
      }
    },
    [activeItem, locationApi, onChange, setQuery, results]
  );

  const wrapperRef = useRef(null);

  const onOutsideClick = useCallback(() => {
    setShowResultsContainer(false);
  }, [setShowResultsContainer]);

  useEffect(() => {
    // If the results container is closed auto select an item
    // If geolocation was selected the active index will be -1 so nothing to auto select
    if (showResultsContainer === false) {
      onItemSelect();
    }
  }, [showResultsContainer, onItemSelect]);

  useOutsideClickHandler(wrapperRef, onOutsideClick, showResultsContainer);

  const onFocus = useCallback(() => {
    setShowResultsContainer(true);
  }, [setShowResultsContainer]);

  const selectItem = useCallback(
    (index) => {
      onItemSelect(index);
    },
    [onItemSelect]
  );

  const onKeyDown = useCallback(
    (evt) => {
      switch (evt.keyCode) {
        case 38:
          // Up arrow
          evt.preventDefault();
          cycleActiveIndex(-1);
          break;
        case 40:
          // Down arrow
          evt.preventDefault();
          cycleActiveIndex(1);
          break;
        case 13:
          // Enter Key
          evt.preventDefault();
          onItemSelect();
          break;
        default:
          // Do nothing
          break;
      }
    },
    [cycleActiveIndex, onItemSelect]
  );

  const onClickGeoLocation = useCallback(() => {
    setResults([]);
    setActiveIndex(-1);
    setQuery("");
    setIsLoading(true);
    setShowResultsContainer(false);

    locationApi
      .getCurrentLocation()
      .then(({ address, lat, lng }) => {
        setQuery(address);
        onChange(address, { lat, lng });
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoading(false);
      });
  }, [locationApi, onChange, setQuery]);

  const isMobileLayout = useMatchMediaMatches("(max-width: 639px)");

  const showInWrapper = showResultsContainer && isMobileLayout;
  useModalHook(showInWrapper, "modal-open");

  useEffect(() => {
    if (showResultsContainer) {
      searchInputRef.current.focus();
    }
  }, [searchInputRef, showInWrapper, showResultsContainer]);

  const onCancel = useCallback(() => {
    setResults([]);
    setQuery(value);
    setActiveIndex(-1);
    setShowResultsContainer(false);
  }, [setQuery, value]);

  return (
    <ConditionalWrapper
      condition={showInWrapper}
      wrapper={(children) => {
        return createPortal(children, document.getElementById("portal"));
      }}
    >
      <LocationSearchInputUI
        key="searchInput"
        wrapperRef={wrapperRef}
        inputRef={searchInputRef}
        query={query}
        results={isLoading && results.length === 0 ? loadingMessage : results}
        isLoading={isLoading}
        activeItem={activeItem}
        onSearchQueryChange={onSearchQueryChange}
        onKeyDown={onKeyDown}
        setActiveIndex={setActiveIndex}
        selectItem={selectItem}
        onFocus={onFocus}
        showResultsContainer={showResultsContainer}
        onClickGeoLocation={onClickGeoLocation}
        isFullscreen={showInWrapper}
        onCancel={onCancel}
        invalid={invalid}
      />
    </ConditionalWrapper>
  );
};

export default LocationSearchInput;
